import React, { useState, useEffect, useRef } from 'react';
import { Form } from 'react-bootstrap';
import Calendar from './calendar.component';
import { PRICING_TYPE_OPTIONS } from './pricing-type-options.constants';
import { Tooltip } from './common/tooltip.component';
import '../scss/create-invoice.scss';
import '../pages/timesheet/timeSheetsPage/timeSheetsPage.scss';
interface ModalFormData {
  hoursPerDay: string;
  hourlyPrice: string;
  showDates: string;
  serviceDescription: string;
  pricingType: string;
}

interface AutoGenerateItemsModalProps {
  isOpen: boolean;
  onClose: () => void;
  freelancerId: string;
  freelancerName: string;
  onGenerate: (modalData: ModalFormData, selectedDates: Date[]) => void;
  isRegularEngagement: boolean;
  currency: string;
  t: (key: string) => string;
  showDatesOptions: Array<{ label: string; value: string }>;
  initialData?: ModalFormData;
  initialSelectedDates?: Date[];
}

const AutoGenerateItemsModal: React.FC<AutoGenerateItemsModalProps> = ({
  isOpen,
  onClose,
  freelancerId,
  freelancerName,
  onGenerate,
  isRegularEngagement,
  currency,
  t,
  showDatesOptions,
  initialData,
  initialSelectedDates,
}) => {
  // Form state - initialize with initialData if provided
  const [formData, setFormData] = useState<ModalFormData>(initialData || {
    hoursPerDay: '08:00',
    hourlyPrice: '',
    showDates: 'yes',
    serviceDescription: '',
    pricingType: 'Tim',
  });

  const [selectedDates, setSelectedDates] = useState<Date[]>(initialSelectedDates || []);
  const [workDatesDisplay, setWorkDatesDisplay] = useState<string>('');
  const [showInternalCalendar, setShowInternalCalendar] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [isMobile, setIsMobile] = useState(false);
  
  // Dropdown states
  const [isPricingTypeDropdownOpen, setIsPricingTypeDropdownOpen] = useState(false);
  
  // Refs
  const pricingTypeDropdownRef = useRef<HTMLDivElement>(null);

  // Update form when initialData changes (when modal reopens)
  useEffect(() => {
    if (isOpen) {
      if (initialData) {
        setFormData(initialData);
      }
      if (initialSelectedDates && initialSelectedDates.length > 0) {
        setSelectedDates(initialSelectedDates);
        setWorkDatesDisplay(formatDateRange(initialSelectedDates));
      }
    }
  }, [isOpen, initialData, initialSelectedDates]);

  // Update hours per day when pricing type changes
  useEffect(() => {
    if (formData.pricingType !== 'Tim') {
      // For non-hourly pricing types, set hours to 1
      setFormData(prev => ({ ...prev, hoursPerDay: '01:00' }));
    }
  }, [formData.pricingType]);

  // Detect mobile screen
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Handle click outside dropdowns
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (pricingTypeDropdownRef.current && !pricingTypeDropdownRef.current.contains(event.target as Node)) {
        setIsPricingTypeDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Format dates for display
  const formatDateRange = (dates: Date[]) => {
    if (dates.length === 0) return '';
    
    const sortedDates = [...dates].sort((a, b) => a.getTime() - b.getTime());
    const startDate = sortedDates[0];
    const endDate = sortedDates[sortedDates.length - 1];
    
    const formatDate = (date: Date) => {
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const year = date.getFullYear();
      return `${month}/${day}/${year}`;
    };
    
    if (dates.length === 1) {
      return formatDate(startDate);
    }
    
    return `${formatDate(startDate)} - ${formatDate(endDate)}`;
  };

  // Handle date selection from calendar
  const handleDateSelect = (startDate: Date, endDate?: Date, dates?: Date[]) => {
    const selectedDateArray = dates || (startDate ? [startDate] : []);
    setSelectedDates(selectedDateArray);
    setWorkDatesDisplay(formatDateRange(selectedDateArray));
    // Don't close calendar - let user select multiple dates
    
    // Clear date error if dates are selected
    if (selectedDateArray.length > 0 && errors.dates) {
      setErrors(prev => ({ ...prev, dates: '' }));
    }
  };

  // Validation
  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    // Hours per day validation
    if (!formData.hoursPerDay || formData.hoursPerDay.trim() === '') {
      newErrors.hoursPerDay = t('HOURS_REQUIRED') || 'Hours per day is required';
    } else {
      const timeRegex = /^([0-1]?[0-9]|2[0-3]):([0-5][0-9])$/;
      if (!timeRegex.test(formData.hoursPerDay)) {
        newErrors.hoursPerDay = t('HOURS_INVALID_FORMAT') || 'Invalid time format. Use HH:MM';
      }
    }

    // Hourly price validation
    if (!formData.hourlyPrice || formData.hourlyPrice.trim() === '') {
      newErrors.hourlyPrice = t('PRICE_REQUIRED') || 'Hourly price is required';
    } else if (parseFloat(formData.hourlyPrice) <= 0) {
      newErrors.hourlyPrice = t('PRICE_MUST_BE_POSITIVE') || 'Price must be greater than 0';
    }

    // Dates validation
    if (selectedDates.length === 0) {
      newErrors.dates = t('PLEASE_SELECT_DATES') || 'Please select at least one date';
    }

    // Service description validation
    if (!formData.serviceDescription.trim()) {
      newErrors.serviceDescription = t('TASK_DETAILS_REQUIRED') || 'Service description is required';
    } else {
      const latinRegex = /^[A-Za-zÅÄÖåäö0-9\s\.,!?'"\-()&]*$/;
      if (!latinRegex.test(formData.serviceDescription)) {
        newErrors.serviceDescription = t('ONLY_LATIN_CHARACTERS_ALLOWED') || 'Only Latin characters are allowed';
      } else if (formData.serviceDescription.length > 42) {
        newErrors.serviceDescription = t('SERVICE_DESCRIPTION_MAX_CHARS') || 'Maximum 42 characters allowed';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle generate
  const handleGenerate = () => {
    if (!validateForm()) {
      return;
    }

    onGenerate(formData, selectedDates);
    onClose();
    
    // Reset form
    setFormData({
      hoursPerDay: '08:00',
      hourlyPrice: '',
      showDates: 'yes',
      serviceDescription: '',
      pricingType: 'Tim',
    });
    setSelectedDates([]);
    setWorkDatesDisplay('');
    setErrors({});
  };

  // Handle cancel
  const handleCancel = () => {
    onClose();
    
    // Reset form
    setFormData({
      hoursPerDay: '08:00',
      hourlyPrice: '',
      showDates: 'yes',
      serviceDescription: '',
      pricingType: 'Tim',
    });
    setSelectedDates([]);
    setWorkDatesDisplay('');
    setErrors({});
  };

  if (!isOpen) return null;

  return (
    <>
      <style>{`
        @media (max-width: 768px) {
          .auto-generate-modal-container {
            padding: 8px !important;
          }
          .auto-generate-modal {
            width: 100% !important;
            max-width: 100% !important;
            border-radius: 12px !important;
            margin: 0 !important;
          }
          .auto-generate-modal-header {
            padding: 16px !important;
          }
          .auto-generate-modal-header h3 {
            font-size: 18px !important;
          }
          .auto-generate-modal-header p {
            font-size: 13px !important;
          }
          .auto-generate-modal-body {
            padding: 16px !important;
          }
          .auto-generate-grid-2col {
            grid-template-columns: 1fr !important;
          }
          .auto-generate-footer {
            padding: 12px 16px !important;
            flex-direction: column !important;
          }
          .auto-generate-footer button {
            width: 100% !important;
            justify-content: center !important;
          }
        }
        @media (max-width: 480px) {
          .auto-generate-modal-header h3 {
            font-size: 16px !important;
          }
          .auto-generate-modal-header p {
            font-size: 12px !important;
          }
          .date-chips-container {
            max-height: 120px !important;
          }
        }
      `}</style>
      <div
        className="auto-generate-modal-container"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          padding: '16px',
        }}
      >
        <div
          className="auto-generate-modal"
          style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            width: '854px',
            maxWidth: '90%',
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0px 4px 8px 0px rgba(0, 0, 0, 0.12), 0px 2px 4px 0px rgba(0, 0, 0, 0.08)',
            fontFamily: "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
          }}
        >
        {/* Header - Fixed */}
        <div className="auto-generate-modal-header" style={{ 
          padding: '12px 16px', 
          borderBottom: '1px solid #eaebf0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexShrink: 0
        }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <h3 style={{ 
              margin: 0, 
              fontSize: '16px', 
              fontWeight: 600,
              lineHeight: 'normal',
              color: '#000000',
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              letterSpacing: '-0.16px'
            }}>
              {t('AUTO_GENERATE_ITEMS') || 'Auto Generate Line Items'}
            </h3>
            <p style={{
              margin: 0,
              fontSize: '14px',
              fontWeight: 300,
              lineHeight: '1.4',
              color: '#252b37',
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              letterSpacing: '0.07px'
            }}>
              {t('AUTO_GENERATE_DESCRIPTION') || 'Configure the parameters to automatically generate invoice line items for selected dates.'}
            </p>
          </div>
          <button
            onClick={handleCancel}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '0',
              width: '24px',
              height: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
              flexShrink: 0
            }}
            aria-label="Close"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 6L6 18M6 6L18 18" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>

        {/* Body - Scrollable */}
        <div className="auto-generate-modal-body" style={{ 
          padding: '24px', 
          display: 'flex', 
          flexDirection: 'column', 
          gap: '24px',
          overflowY: 'auto',
          flex: 1,
          minHeight: 0
        }}>
         
 {/* Select Dates - Full Width */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <label style={{
              color: 'var(--text-icons-light-base-main, #252525)',
              fontFamily: '"Plus Jakarta Sans"',
              fontSize: '14px',
              fontStyle: 'normal',
              fontWeight: 600,
              lineHeight: '20px',
              display: 'flex',
              alignItems: 'center',
              // gap: '6px'
            }}>
              {t('SELECT_DATES') || 'Select Dates'}
              {/* <span style={{ color: '#dc3545', marginLeft: '4px' }}>*</span> */}
              <Tooltip content={t('DATES_TOOLTIP') || 'Indicate the dates when the work was performed.'} position="top">
                <span style={{ 
                  marginLeft: '4px', 
                  color: 'var(--Brand-500, #6750B8)', 
                  fontFamily: '"Plus Jakarta Sans"', 
                  fontSize: '14px', 
                  fontStyle: 'normal',
                  fontWeight: 700, 
                  lineHeight: '20px'
                }}>ⓘ</span>
              </Tooltip>
            </label>
            <div style={{ position: 'relative' }}>
              <div
                style={{
                  height: '44px',
                  padding: '12px 16px',
                  borderRadius: '10px',
                  border: errors.dates ? '1px solid #dc3545' : '1px solid #eaebf0',
                  background: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  cursor: 'pointer',
                  fontSize: '15px',
                  fontWeight: 500,
                  color: workDatesDisplay ? '#252525' : '#68727d',
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  transition: 'all 0.2s ease'
                }}
                onClick={() => setShowInternalCalendar(!showInternalCalendar)}
                onMouseEnter={(e) => {
                  if (!errors.dates) {
                    e.currentTarget.style.borderColor = '#b8b0d4';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = errors.dates ? '#dc3545' : '#eaebf0';
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M2.5 8.33073H17.5M6.66667 4.9974V1.66406M13.3333 4.9974V1.66406M7.83333 18.3307H12.1667C14.0335 18.3307 14.9669 18.3307 15.68 17.9674C16.3072 17.6478 16.8171 17.1379 17.1367 16.5107C17.5 15.7977 17.5 14.8642 17.5 12.9974V8.66406C17.5 6.79722 17.5 5.8638 17.1367 5.15076C16.8171 4.52355 16.3072 4.01362 15.68 3.69404C14.9669 3.33073 14.0335 3.33073 12.1667 3.33073H7.83333C5.96649 3.33073 5.03307 3.33073 4.32003 3.69404C3.69282 4.01362 3.18289 4.52355 2.86331 5.15076C2.5 5.8638 2.5 6.79722 2.5 8.66406V12.9974C2.5 14.8642 2.5 15.7977 2.86331 16.5107C3.18289 17.1379 3.69282 17.6478 4.32003 17.9674C5.03307 18.3307 5.96649 18.3307 7.83333 18.3307Z" stroke="#68727D" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                  <span>{workDatesDisplay || t('CLICK_TO_SELECT_DATES') || 'Click to select dates'}</span>
                </div>
              </div>
              {showInternalCalendar && (
                <div style={{ 
                  position: 'relative', 
                  marginTop: '8px', 
                  zIndex: 9999,
                  width: '100%'
                }}>
                  <div style={{
                    transform: isMobile ? 'scale(0.75)' : 'scale(0.85)',
                    transformOrigin: 'top left',
                    // width: isMobile ? '133.33%' : '117.65%'
                  }}>
                    {/* Calendar Tooltip - shows when no dates selected */}
                    {selectedDates.length === 0 && (
                      <div style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        zIndex: 10001,
                        backgroundColor: '#252525',
                        color: 'white',
                        padding: '8px 12px',
                        borderRadius: '6px',
                        fontSize: isMobile ? '11px' : '12px',
                        fontWeight: 400,
                        fontFamily: "'Plus Jakarta Sans', sans-serif",
                        whiteSpace: isMobile ? 'normal' : 'nowrap',
                        maxWidth: isMobile ? '200px' : 'none',
                        textAlign: 'center',
                        boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.15)',
                        pointerEvents: 'none'
                      }}>
                        {t('CALENDAR_TOOLTIP') || 'Click to select a date. Drag to select multiple dates.'}
                      </div>
                    )}
                    <Calendar
                      isOpen={showInternalCalendar}
                      onClose={() => setShowInternalCalendar(false)}
                      onDateSelect={handleDateSelect}
                      initialSelectedDates={selectedDates}
                      allowRange={false}
                      allowMultiple={true}
                      inline={true}
                    />
                  </div>
                </div>
              )}
            </div>
            {errors.dates && (
              <div style={{ color: '#dc3545', fontSize: '12px', marginTop: '4px' }}>
                {errors.dates}
              </div>
            )}

            {/* Selected Dates Chips */}
            {selectedDates.length > 0 && (
              <div className="date-chips-container" style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '8px',
                marginTop: '8px',
                maxHeight: '150px',
                overflowY: 'auto'
              }}>
                {selectedDates.map((date, index) => (
                  <div
                    key={index}
                    style={{
                      height: '36px',
                      padding: '8px 12px',
                      borderRadius: '20px',
                      background: '#edebf5',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      fontSize: '14px',
                      fontWeight: 500,
                      color: '#544196',
                      fontFamily: "'Plus Jakarta Sans', sans-serif"
                    }}
                  >
                    <span>{new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                    <button
                      onClick={() => {
                        const newDates = selectedDates.filter((_, i) => i !== index);
                        setSelectedDates(newDates);
                        if (newDates.length === 0) {
                          setWorkDatesDisplay('');
                        } else if (newDates.length === 1) {
                          setWorkDatesDisplay(new Date(newDates[0]).toLocaleDateString('en-US'));
                        } else {
                          const sortedDates = newDates.map(d => new Date(d)).sort((a, b) => a.getTime() - b.getTime());
                          const startDate = sortedDates[0].toLocaleDateString('en-US');
                          const endDate = sortedDates[sortedDates.length - 1].toLocaleDateString('en-US');
                          setWorkDatesDisplay(`${startDate} - ${endDate}`);
                        }
                      }}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '0',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#544196'
                      }}
                    >
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path
                          d="M12 4L4 12M4 4L12 12"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
          {/* Hours per Day and Hourly Price - Two Columns */}
          <div className="auto-generate-grid-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            {/* Hours per day */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <label style={{
                color: 'var(--text-icons-light-base-main, #252525)',
                fontFamily: '"Plus Jakarta Sans"',
                fontSize: '14px',
                fontStyle: 'normal',
                fontWeight: 600,
                lineHeight: '20px',
                display: 'flex',
                alignItems: 'center',
                // gap: '6px'
              }}>
                {t('HOURS_PER_DAY') || 'Hours per Day'}
                {/* <span style={{ color: '#dc3545' }}>*</span> */}
                <Tooltip content={t('HOURS_PER_DAY_TOOLTIP') || 'Enter the number of hours you typically work per day for this project.'} position="top">
                  <span style={{
                    marginLeft: '4px',
                    color: 'var(--Brand-500, #6750B8)',
                    fontFamily: '"Plus Jakarta Sans"',
                    fontSize: '14px',
                    fontStyle: 'normal',
                    fontWeight: 700,
                    lineHeight: '20px'
                  }}>ⓘ</span>
                </Tooltip>
              </label>
              <input
                type="text"
                placeholder="08:00"
                disabled={formData.pricingType !== 'Tim'}
                style={{
                  height: '44px',
                  padding: '12px 16px',
                  borderRadius: '10px',
                  border: errors.hoursPerDay ? '1px solid #dc3545' : '1px solid #eaebf0',
                  background: formData.pricingType !== 'Tim' ? '#f8f9fa' : 'white',
                  fontSize: '15px',
                  fontWeight: 500,
                  color: formData.pricingType !== 'Tim' ? '#68727d' : '#252525',
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  outline: 'none',
                  transition: 'all 0.2s ease',
                  cursor: formData.pricingType !== 'Tim' ? 'not-allowed' : 'text'
                }}
                value={formData.hoursPerDay}
                onChange={(e) => {
                  if (formData.pricingType !== 'Tim') return;
                  const value = e.target.value;
                  if (value === '' || /^\d{0,2}([\.:])?(\d{0,2})?$/.test(value)) {
                    if (value.includes(':')) {
                      const [hours, minutes] = value.split(':');
                      const h = parseInt(hours || '0');
                      const m = parseInt(minutes || '0');
                      if (value === '' || ((hours === '' || h <= 23) && (minutes === '' || m <= 59))) {
                        setFormData({ ...formData, hoursPerDay: value });
                        if (errors.hoursPerDay) setErrors({ ...errors, hoursPerDay: '' });
                      }
                    } else {
                      const numValue = parseFloat(value);
                      if (value === '' || isNaN(numValue) || (numValue >= 0 && numValue <= 24)) {
                        setFormData({ ...formData, hoursPerDay: value });
                        if (errors.hoursPerDay) setErrors({ ...errors, hoursPerDay: '' });
                      }
                    }
                  }
                }}
                onFocus={(e) => {
                  if (formData.pricingType !== 'Tim') return;
                  if (!errors.hoursPerDay) {
                    e.currentTarget.style.borderColor = '#6750b8';
                    e.currentTarget.style.boxShadow = '0 0 0 3px rgba(103, 80, 184, 0.1)';
                  } else {
                    e.currentTarget.style.borderColor = '#dc3545';
                    e.currentTarget.style.boxShadow = '0 0 0 3px rgba(220, 53, 69, 0.1)';
                  }
                }}
                onBlur={(e) => {
                  if (formData.pricingType !== 'Tim') return;
                  e.currentTarget.style.borderColor = errors.hoursPerDay ? '#dc3545' : '#eaebf0';
                  e.currentTarget.style.boxShadow = 'none';
                  const value = e.target.value;
                  if (!value) return;

                  if (value.includes(':')) {
                    const [hours, minutes] = value.split(':');
                    const h = hours.padStart(2, '0');
                    const m = (minutes || '00').padStart(2, '0');
                    setFormData({ ...formData, hoursPerDay: `${h}:${m}` });
                  } else if (value.includes('.')) {
                    const parts = value.split('.');
                    const hours = parseInt(parts[0] || '0');
                    const decimal = parseInt(parts[1] || '0');

                    if (decimal <= 59) {
                      setFormData({ ...formData, hoursPerDay: `${hours.toString().padStart(2, '0')}:${decimal.toString().padStart(2, '0')}` });
                    } else {
                      const totalMinutes = Math.round((parseFloat(value) - hours) * 60);
                      setFormData({ ...formData, hoursPerDay: `${hours.toString().padStart(2, '0')}:${totalMinutes.toString().padStart(2, '0')}` });
                    }
                  } else {
                    setFormData({ ...formData, hoursPerDay: value.padStart(2, '0') + ':00' });
                  }
                }}
                onMouseEnter={(e) => {
                  if (formData.pricingType !== 'Tim') return;
                  if (document.activeElement !== e.currentTarget && !errors.hoursPerDay) {
                    e.currentTarget.style.borderColor = '#b8b0d4';
                  }
                }}
                onMouseLeave={(e) => {
                  if (formData.pricingType !== 'Tim') return;
                  if (document.activeElement !== e.currentTarget) {
                    e.currentTarget.style.borderColor = errors.hoursPerDay ? '#dc3545' : '#eaebf0';
                  }
                }}
              />
              {errors.hoursPerDay && (
                <div style={{ color: '#dc3545', fontSize: '12px', marginTop: '4px' }}>
                  {errors.hoursPerDay}
                </div>
              )}
            </div>

            {/* Hourly Price */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <label style={{
                color: 'var(--text-icons-light-base-main, #252525)',
                fontFamily: '"Plus Jakarta Sans"',
                fontSize: '14px',
                fontStyle: 'normal',
                fontWeight: 600,
                lineHeight: '20px',
                display: 'flex',
                alignItems: 'center',
                // gap: '6px'
              }}>
                {t('PRICE') || 'Hourly Price'}
                {/* <span style={{ color: '#dc3545' }}>*</span> */}
                <Tooltip content={t('HOURLY_PRICE_TOOLTIP') || 'Set the amount you charge per hour of work.'} position="top">
                  <span style={{
                    marginLeft: '4px',
                    color: 'var(--Brand-500, #6750B8)',
                    fontFamily: '"Plus Jakarta Sans"',
                    fontSize: '14px',
                    fontStyle: 'normal',
                    fontWeight: 700,
                    lineHeight: '20px'
                  }}>ⓘ</span>
                </Tooltip>
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  type="text"
                  placeholder={t('ENTER_AMOUNT') || 'Enter amount'}
                  style={{
                    width: '100%',
                    height: '44px',
                    padding: '12px 50px 12px 16px',
                    borderRadius: '10px',
                    border: errors.hourlyPrice ? '1px solid #dc3545' : '1px solid #eaebf0',
                    background: 'white',
                    fontSize: '15px',
                    fontWeight: 500,
                    color: '#252525',
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                    outline: 'none',
                    transition: 'all 0.2s ease'
                  }}
                  value={formData.hourlyPrice}
                  onKeyDown={(e) => {
                    if (e.key === '-' || e.key === 'e' || e.key === 'E') {
                      e.preventDefault();
                    }
                  }}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === '' || /^\d*\.?\d*$/.test(value)) {
                      setFormData({ ...formData, hourlyPrice: value });
                      if (errors.hourlyPrice) setErrors({ ...errors, hourlyPrice: '' });
                    }
                  }}
                  onFocus={(e) => {
                    if (!errors.hourlyPrice) {
                      e.currentTarget.style.borderColor = '#6750b8';
                      e.currentTarget.style.boxShadow = '0 0 0 3px rgba(103, 80, 184, 0.1)';
                    } else {
                      e.currentTarget.style.borderColor = '#dc3545';
                      e.currentTarget.style.boxShadow = '0 0 0 3px rgba(220, 53, 69, 0.1)';
                    }
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = errors.hourlyPrice ? '#dc3545' : '#eaebf0';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                  onMouseEnter={(e) => {
                    if (document.activeElement !== e.currentTarget && !errors.hourlyPrice) {
                      e.currentTarget.style.borderColor = '#b8b0d4';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (document.activeElement !== e.currentTarget) {
                      e.currentTarget.style.borderColor = errors.hourlyPrice ? '#dc3545' : '#eaebf0';
                    }
                  }}
                />
                <span style={{
                  position: 'absolute',
                  right: '16px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  fontSize: '15px',
                  fontWeight: 500,
                  color: '#68727d',
                  fontFamily: "'Plus Jakarta Sans', sans-serif"
                }}>
                  {currency}
                </span>
              </div>
              {errors.hourlyPrice && (
                <div style={{ color: '#dc3545', fontSize: '12px', marginTop: '4px' }}>
                  {errors.hourlyPrice}
                </div>
              )}
            </div>
          </div>

         
 {/* Pricing Type - Full Width */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <label style={{
              color: 'var(--text-icons-light-base-main, #252525)',
              fontFamily: '"Plus Jakarta Sans"',
              fontSize: '14px',
              fontStyle: 'normal',
              fontWeight: 600,
              lineHeight: '20px',
              display: 'flex',
              alignItems: 'center',
              // gap: '6px'
            }}>
              {t('PRICING_TYPE') || 'Price Type'}
              <Tooltip content={t('PRICING_TYPE_TOOLTIP') || 'Choose how your pricing is calculated'} position="top">
                <span style={{ 
                  marginLeft: '4px', 
                  color: 'var(--Brand-500, #6750B8)', 
                  fontFamily: '"Plus Jakarta Sans"', 
                  fontSize: '14px', 
                  fontStyle: 'normal',
                  fontWeight: 700, 
                  lineHeight: '20px'
                }}>ⓘ</span>
              </Tooltip>
            </label>
            <div className="custom-dropdown" ref={pricingTypeDropdownRef} style={{ position: 'relative' }}>
              <div
                style={{
                  height: '44px',
                  padding: '12px 16px',
                  borderRadius: '10px',
                  border: '1px solid #eaebf0',
                  background: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  cursor: 'pointer',
                  fontSize: '15px',
                  fontWeight: 500,
                  color: '#252525',
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  transition: 'all 0.2s ease'
                }}
                onClick={() => setIsPricingTypeDropdownOpen(!isPricingTypeDropdownOpen)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#b8b0d4';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#eaebf0';
                }}
              >
                <span>
                  {t(PRICING_TYPE_OPTIONS.find(opt => opt.value === formData.pricingType)?.articleName || 'HOURLY_WAGES')}
                </span>
                <svg
                  style={{
                    transform: isPricingTypeDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s'
                  }}
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                >
                  <path
                    d="M5 7.5L10 12.5L15 7.5"
                    stroke="#68727d"
                    strokeWidth="1.67"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>

              {isPricingTypeDropdownOpen && (
                <div style={{
                  position: 'absolute',
                  top: 'calc(100% + 4px)',
                  left: 0,
                  right: 0,
                  background: 'white',
                  borderRadius: '10px',
                  border: '1px solid #eaebf0',
                  boxShadow: '0px 4px 8px 0px rgba(0, 0, 0, 0.12)',
                  zIndex: 1000,
                  maxHeight: '200px',
                  overflowY: 'auto'
                }}>
                  {PRICING_TYPE_OPTIONS.map((option) => (
                    <div
                      key={option.id}
                      style={{
                        padding: '12px 16px',
                        cursor: 'pointer',
                        fontSize: '15px',
                        fontWeight: 500,
                        color: formData.pricingType === option.value ? '#6750b8' : '#252525',
                        background: formData.pricingType === option.value ? '#f4f3f9' : 'white',
                        fontFamily: "'Plus Jakarta Sans', sans-serif"
                      }}
                      onMouseEnter={(e) => {
                        if (formData.pricingType !== option.value) {
                          e.currentTarget.style.background = '#f8f9fa';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (formData.pricingType !== option.value) {
                          e.currentTarget.style.background = 'white';
                        }
                      }}
                      onClick={() => {
                        setFormData({ ...formData, pricingType: option.value });
                        setIsPricingTypeDropdownOpen(false);
                      }}
                    >
                      {t(option.articleName)}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          {/* Service Name - Full Width */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <label style={{
                color: 'var(--text-icons-light-base-main, #252525)',
                fontFamily: '"Plus Jakarta Sans"',
                fontSize: '14px',
                fontStyle: 'normal',
                fontWeight: 600,
                lineHeight: '20px',
                display: 'flex',
                alignItems: 'center',
                // gap: '6px'
              }}>
                {t('SERVICE_DESCRIPTION') || 'Service Name'}
                {/* <span style={{ color: '#dc3545' }}>*</span> */}
                <Tooltip content={t('SERVICE_DESCRIPTION_TOOLTIP') || 'Provide a brief description of the work or service performed.'} position="top">
                  <span style={{
                    marginLeft: '4px',
                    color: 'var(--Brand-500, #6750B8)',
                    fontFamily: '"Plus Jakarta Sans"',
                    fontSize: '14px',
                    fontStyle: 'normal',
                    fontWeight: 700,
                    lineHeight: '20px'
                  }}>ⓘ</span>
                </Tooltip>
              </label>
              <input
                type="text"
                placeholder={t('ENTER_SERVICE_DESCRIPTION') || 'Enter service name'}
                style={{
                  height: '44px',
                  padding: '12px 16px',
                  borderRadius: '10px',
                  border: errors.serviceDescription ? '1px solid #dc3545' : '1px solid #eaebf0',
                  background: 'white',
                  fontSize: '15px',
                  fontWeight: 500,
                  color: '#252525',
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  outline: 'none',
                  transition: 'all 0.2s ease'
                }}
                value={formData.serviceDescription}
                maxLength={42}
                onChange={(e) => {
                  const value = e.target.value;
                  const latinPattern = /^[A-Za-zÅÄÖåäö0-9\s\.,!?'"\-()&]*$/;

                  if (value.length <= 42 && (value === '' || latinPattern.test(value))) {
                    setFormData({ ...formData, serviceDescription: value });
                    if (errors.serviceDescription) setErrors({ ...errors, serviceDescription: '' });
                  }
                }}
                onFocus={(e) => {
                  if (!errors.serviceDescription) {
                    e.currentTarget.style.borderColor = '#6750b8';
                    e.currentTarget.style.boxShadow = '0 0 0 3px rgba(103, 80, 184, 0.1)';
                  } else {
                    e.currentTarget.style.borderColor = '#dc3545';
                    e.currentTarget.style.boxShadow = '0 0 0 3px rgba(220, 53, 69, 0.1)';
                  }
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = errors.serviceDescription ? '#dc3545' : '#eaebf0';
                  e.currentTarget.style.boxShadow = 'none';
                }}
                onMouseEnter={(e) => {
                  if (document.activeElement !== e.currentTarget && !errors.serviceDescription) {
                    e.currentTarget.style.borderColor = '#b8b0d4';
                  }
                }}
                onMouseLeave={(e) => {
                  if (document.activeElement !== e.currentTarget) {
                    e.currentTarget.style.borderColor = errors.serviceDescription ? '#dc3545' : '#eaebf0';
                  }
                }}
              />
              {errors.serviceDescription && (
                <div style={{ color: '#dc3545', fontSize: '12px', marginTop: '4px' }}>
                  {errors.serviceDescription}
                </div>
              )}
              <small style={{ fontSize: '12px', color: '#68727d', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                {formData.serviceDescription.length}/42 {t('CHARACTERS') || 'characters'}
              </small>
            </div>

          {/* Summary Box */}
          {selectedDates.length > 0 && formData.hoursPerDay && formData.hourlyPrice && (
            <div style={{
              padding: '16px',
              borderRadius: '10px',
              background: '#f4f3f9',
              display: 'flex',
              flexDirection: 'column',
              gap: '8px'
            }}>
              <h4 style={{
                margin: 0,
                fontSize: '14px',
                fontWeight: 600,
                lineHeight: '20px',
                color: '#252525',
                fontFamily: "'Plus Jakarta Sans', sans-serif"
              }}>
                {t('AUTO_GENERATE_ITEMS') || 'Auto Generate Line Items'}
              </h4>
              <p style={{
                margin: 0,
                fontSize: '14px',
                fontWeight: 300,
                lineHeight: '20px',
                color: '#68727d',
                fontFamily: "'Plus Jakarta Sans', sans-serif"
              }}>
                {t('SUMMARY_GENERATE_TEXT')} {selectedDates.length} {selectedDates.length !== 1 ? t('SUMMARY_LINE_ITEMS') : t('SUMMARY_LINE_ITEM')}, {t('SUMMARY_EACH_WITH')} {formData.hoursPerDay} {t('SUMMARY_HOURS_AT')} {formData.hourlyPrice} {currency}/{formData.pricingType || 'Tim'}
              </p>
              <p style={{
                margin: 0,
                fontSize: '14px',
                fontWeight: 700,
                lineHeight: '20px',
                color: '#6750b8',
                fontFamily: "'Plus Jakarta Sans', sans-serif"
              }}>
                {t('ESTIMATED_TOTAL')}: {(() => {
                  const [hours, minutes] = formData.hoursPerDay.split(':').map(Number);
                  const totalHours = hours + (minutes / 60);
                  const pricePerItem = totalHours * parseFloat(formData.hourlyPrice || '0');
                  const total = pricePerItem * selectedDates.length;
                  return total.toFixed(2);
                })()} {currency}
              </p>
            </div>
          )}
        </div>

        {/* Footer - Fixed */}
        <div className="auto-generate-footer" style={{ 
          padding: '16px 24px',
          borderTop: '1px solid #eaebf0',
          display: 'flex', 
          justifyContent: 'flex-end', 
          gap: '12px',
          flexShrink: 0
        }}>
          <button
            onClick={handleCancel}
            style={{
              height: '40px',
              padding: '10px 24px',
              borderRadius: '25px',
              border: '1px solid #6750b8',
              background: 'white',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 600,
              color: '#6750b8',
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#f4f3f9';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'white';
            }}
          >
            {t('CANCEL') || 'Cancel'}
          </button>
          <button
            onClick={handleGenerate}
            style={{
              height: '40px',
              padding: '10px 24px',
              borderRadius: '25px',
              border: 'none',
              background: '#6750b8',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 600,
              color: 'white',
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#544196';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = '#6750b8';
            }}
          >
           <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
  <path d="M8.65185 1.56501C8.78087 1.25958 9.2137 1.25958 9.34273 1.56501L11.3882 6.4069C11.4262 6.49684 11.4977 6.56842 11.5877 6.60642L16.4296 8.65185C16.735 8.78087 16.735 9.2137 16.4296 9.34273L11.5877 11.3882C11.4977 11.4262 11.4262 11.4977 11.3882 11.5877L9.34273 16.4296C9.2137 16.735 8.78087 16.735 8.65185 16.4296L6.60642 11.5877C6.56842 11.4977 6.49684 11.4262 6.4069 11.3882L1.56501 9.34273C1.25958 9.2137 1.25958 8.78087 1.56501 8.65185L6.4069 6.60642C6.49684 6.56842 6.56842 6.49684 6.60642 6.4069L8.65185 1.56501Z" stroke="white" stroke-width="2"/>
</svg>
            {t('GENERATES') || 'Generate Items'}
          </button>
        </div>
      </div>
    </div>
    </>
  );
};

export default AutoGenerateItemsModal;
